import React from 'react';
import Spinner from './Spinner';

function FormResponsesModal({
  participant,
  schema,
  onApprove,
  onReject,
  onClose,
  approveLoading = false,
  rejectLoading = false
}) {
  if (!participant || !schema) return null;

  const responses = participant.form_responses || {};

  const getResponseDisplay = (field) => {
    const response = responses[field.id];

    if (response === undefined || response === null || response === '') {
      return <span className="text-gray-400 italic">Non risposto</span>;
    }

    if (Array.isArray(response)) {
      if (response.length === 0) {
        return <span className="text-gray-400 italic">Nessuna selezione</span>;
      }
      return (
        <ul className="list-disc list-inside">
          {response.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
    }

    return response;
  };

  const isLoading = approveLoading || rejectLoading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Risposte al Modulo
            </h2>
            <p className="text-gray-600">
              {participant.is_external
                ? participant.external_name
                : `${participant.nome} ${participant.cognome}`}
              {participant.is_external && participant.added_by_user_id && (
                <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                  Esterno
                </span>
              )}
              {participant.is_external && !participant.added_by_user_id && (
                <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                  Ospite
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {schema.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessuna domanda nel modulo</p>
          ) : (
            <div className="space-y-6">
              {schema.map((field, index) => (
                <div key={field.id} className="border-b border-gray-100 pb-4 last:border-0">
                  <div className="text-sm text-gray-500 mb-1">Domanda {index + 1}</div>
                  <div className="font-medium text-gray-800 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <div className="text-gray-700 bg-gray-50 rounded-lg p-3">
                    {getResponseDisplay(field)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Stato attuale:{' '}
              <span className={`font-medium ${
                participant.stato === 'approvata' ? 'text-green-600' :
                participant.stato === 'in_attesa' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {participant.stato === 'approvata' ? 'Approvata' :
                 participant.stato === 'in_attesa' ? 'In Attesa' :
                 'Non Approvata'}
              </span>
            </div>
            <div className="flex gap-3">
              {/* Close button - always visible */}
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm disabled:opacity-50"
              >
                {participant.is_external && participant.added_by_user_id ? 'Chiudi' : 'Annulla'}
              </button>

              {/* Approval buttons - for registered users and guests (not organizer-added externals) */}
              {(!participant.is_external || !participant.added_by_user_id) && participant.stato === 'approvata' && (
                <button
                  onClick={onReject}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {rejectLoading && <Spinner size="sm" color="white" />}
                  {rejectLoading ? 'Revoca...' : 'Revoca Approvazione'}
                </button>
              )}
              {(!participant.is_external || !participant.added_by_user_id) && participant.stato === 'in_attesa' && (
                <>
                  <button
                    onClick={onReject}
                    disabled={isLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    {rejectLoading && <Spinner size="sm" color="white" />}
                    {rejectLoading ? 'Rifiuto...' : 'Rifiuta'}
                  </button>
                  <button
                    onClick={onApprove}
                    disabled={isLoading}
                    className="px-4 py-2 text-white rounded-lg transition font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: isLoading ? '#93C5FD' : '#0E60C6' }}
                    onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
                    onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
                  >
                    {approveLoading && <Spinner size="sm" color="white" />}
                    {approveLoading ? 'Approvazione...' : 'Approva'}
                  </button>
                </>
              )}
              {(!participant.is_external || !participant.added_by_user_id) && participant.stato !== 'approvata' && participant.stato !== 'in_attesa' && (
                <button
                  onClick={onApprove}
                  disabled={isLoading}
                  className="px-4 py-2 text-white rounded-lg transition font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: isLoading ? '#93C5FD' : '#0E60C6' }}
                  onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
                  onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
                >
                  {approveLoading && <Spinner size="sm" color="white" />}
                  {approveLoading ? 'Approvazione...' : 'Approva'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormResponsesModal;
